import { createActionRegistry, type ActionDefinition } from '@command-center/action-sdk';
import type { ConnectorConfig } from '@command-center/connector-sdk';
import type { TableDefinition, ViewDefinition } from '@command-center/data-sdk';
import {
  createModuleRegistry,
  type PortalAppType,
  type PortalModule,
} from '@command-center/module-sdk';
import type { PortalAliceConfig } from './alice';

export * from './alice';
export * from './alice-suggestions';
export * from './alice-registry';
export * from './alice-workflows';
export * from './alice-policy';
export * from './platform-actions';

export interface BrandingConfig {
  name: string;
  logo?: string;
  icon?: string;
  colors?: {
    primary?: string;
    background?: string;
    accent?: string;
  };
}

export interface NavigationItem {
  id: string;
  label: string;
  route: string;
  icon?: string;
  group?: string;
  moduleId?: string;
  /** Marks navigation/page content as intentionally not implemented yet. */
  placeholder?: boolean;
  /** Optional source table or service shown by placeholder pages. */
  sourceName?: string;
  /** Optional one-line subtitle shown next to the page title (e.g. "Daily operating queue"). */
  description?: string;
}

export interface PortalAppConfig {
  appId: string;
  appType: PortalAppType;
  clientId?: string;
  clientName: string;
  branding: BrandingConfig;
  navigation: NavigationItem[];
  navigationVariant?: 'default' | 'workflow-groups';
  collapsibleNavigationGroups?: boolean;
  modules: PortalModule[];
  connectors: ConnectorConfig[];
  tables?: TableDefinition[];
  views?: ViewDefinition[];
  actions?: ActionDefinition[];
  alice?: PortalAliceConfig;
  featureFlags?: Record<string, boolean>;
}

export interface PortalAppDefinition {
  config: PortalAppConfig;
  modules: ReturnType<typeof createModuleRegistry>;
  actions: ReturnType<typeof createActionRegistry>;
}

export function createPortalApp(config: PortalAppConfig): PortalAppDefinition {
  validatePortalAppConfig(config);
  return {
    config,
    modules: createModuleRegistry(config.modules),
    actions: createActionRegistry(config.actions ?? config.modules.flatMap((module) => module.actions ?? [])),
  };
}

export function getEnabledNavigation(config: PortalAppConfig): NavigationItem[] {
  const enabledModuleIds = new Set(
    createModuleRegistry(config.modules)
      .enabledFor(config.appType)
      .map((module) => module.id),
  );

  return config.navigation.filter((item) => !item.moduleId || enabledModuleIds.has(item.moduleId));
}

function validatePortalAppConfig(config: PortalAppConfig): void {
  if (!config.appId.trim()) throw new Error('Portal app id cannot be empty.');
  if (!config.clientName.trim()) throw new Error(`Portal app ${config.appId} must define clientName.`);
  if (!config.branding.name.trim()) throw new Error(`Portal app ${config.appId} must define branding.name.`);

  const moduleIds = new Set<string>();
  for (const module of config.modules) {
    if (moduleIds.has(module.id)) throw new Error(`Duplicate module id in ${config.appId}: ${module.id}`);
    moduleIds.add(module.id);
  }
}
